import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Flame, Snowflake } from "lucide-react";

const MODES = {
  cool: {
    label: "Ice Tech",
    icon: Snowflake,
    title: "Cooling engineered like refrigeration science.",
    copy: "Inverter-driven compressors modulate in 1% steps, holding your setpoint within half a degree while stripping humidity out of the air.",
    image: "https://images.unsplash.com/photo-1608377205619-03a0b4c4e270?crop=entropy&cs=srgb&fm=jpg&q=85",
    accent: "ice",
    specs: [
      "Up to 26 SEER2 — cuts cooling bills up to 40%",
      "Variable-speed inverter compressors",
      "Humidity extraction locked to 45% RH",
      "Whisper operation at 52 dB",
      "Smart zoning per room, app-controlled",
    ],
  },
  heat: {
    label: "Forge Tech",
    icon: Flame,
    title: "Heating forged for the coldest nights.",
    copy: "Modulating gas valves and dual-fuel heat pumps deliver continuous, even warmth — no blasts, no cold corners, no dry air swings.",
    image: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?crop=entropy&cs=srgb&fm=jpg&q=85",
    accent: "forge",
    specs: [
      "98.5% AFUE modulating furnaces",
      "Dual-fuel heat pumps rated to -13°F",
      "Even-heat exchanger — zero cold spots",
      "CO safety shield with live monitoring",
      "10-year parts & labor warranty",
    ],
  },
};

export default function DualClimateMode() {
  const [mode, setMode] = useState("cool");
  const active = MODES[mode];
  const isCool = mode === "cool";

  return (
    <section id="dual" data-testid="dual-mode-container" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-xl">
            <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-ice">02 — Dual-Force Climate</span>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              Pick your force. We master both.
            </h2>
          </div>

          <div className="flex rounded-2xl border border-white/10 bg-navy-800/80 p-1.5 backdrop-blur">
            {Object.entries(MODES).map(([key, m]) => {
              const Icon = m.icon;
              const on = mode === key;
              return (
                <button
                  key={key}
                  data-testid={key === "cool" ? "dual-mode-cooling-tab" : "dual-mode-heating-tab"}
                  onClick={() => setMode(key)}
                  className={`flex items-center gap-2.5 rounded-xl px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
                    on
                      ? key === "cool"
                        ? "bg-gradient-to-br from-ice-lo to-ice text-navy-950"
                        : "bg-gradient-to-br from-forge to-forge-hi text-white"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {m.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid items-stretch gap-6 lg:grid-cols-2"
          >
            <div className="clip-frame relative min-h-[320px] overflow-hidden border border-white/10">
              <img src={active.image} alt={`${active.label} interior`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div
                className={`absolute inset-0 ${
                  isCool
                    ? "bg-gradient-to-tr from-navy-950/90 via-navy-950/30 to-ice/20"
                    : "bg-gradient-to-tr from-navy-950/90 via-navy-950/30 to-forge/25"
                }`}
              />
              <div className="absolute bottom-5 left-6 font-mono text-[9px] uppercase tracking-[0.24em] text-slate-300">
                {isCool ? "FIELD INSTALL — SMART THERMOSTAT ARRAY" : "FIELD INSTALL — FORGE FURNACE BAY"}
              </div>
            </div>

            <div className="flex flex-col justify-center rounded-2xl border border-white/[0.08] bg-navy-800/70 p-8 sm:p-10">
              <h3 className="font-display text-xl font-bold tracking-tight text-slate-50 sm:text-2xl">{active.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{active.copy}</p>
              <ul className="mt-7 space-y-3.5">
                {active.specs.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${isCool ? "text-ice" : "text-forge"}`} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
